import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MANAGED_ROLES, UserFilters as Filters } from '@/types';
import { X } from 'lucide-react';

interface Props {
  filters: Filters;
  onChange: (filters: Filters) => void;
}

const ALL = '__all__';

export function UserFilters({ filters, onChange }: Props) {
  const { t } = useTranslation('users');
  const [searchInput, setSearchInput] = useState(filters.search ?? '');

  // Debounced email-substring search, same pattern as EventFilters — 300ms,
  // empty clears rather than filtering to nothing.
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();
      const next = trimmed.length === 0 ? undefined : trimmed;
      if (next !== filters.search) onChange({ ...filters, search: next });
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const hasActiveFilters = !!filters.search || !!filters.role;

  const clearAll = () => {
    setSearchInput('');
    onChange({});
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Input
        placeholder={t('filters.searchPlaceholder')}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        className="max-w-xs"
      />

      <Select
        value={filters.role ?? ALL}
        onValueChange={(v) => onChange({ ...filters, role: v === ALL ? undefined : v as Filters['role'] })}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder={t('filters.role')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('filters.allRoles')}</SelectItem>
          {MANAGED_ROLES.map((r) => (
            <SelectItem key={r} value={r}>
              {t(`role.${r}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={clearAll} className="gap-1.5">
          <X className="h-4 w-4" />
          {t('filters.clear')}
        </Button>
      )}
    </div>
  );
}
